import { useCharacterStore } from '@/engine/state';
import { aplicarEfectos } from '@/engine/narrative';
import { obtenerSkill, SKILLS_TODAS, type SkillDefinicion } from './skillTreeData';

export type EstadoSkill = 'bloqueada' | 'disponible' | 'desbloqueada';

/**
 * Nivel de ofuscación visual:
 *  - ninguno: se ve tal cual (desbloqueada, disponible, o bloqueada "cercana")
 *  - niebla: blur suave, nombre visible (bloqueada lejana)
 *  - spoiler: blur fuerte y nombre enmascarado (finales — tier 4 con prereqs sin tocar)
 */
export type NivelFog = 'ninguno' | 'niebla' | 'spoiler';

export interface SkillAnotada {
  skill: SkillDefinicion;
  estado: EstadoSkill;
  motivo: string | null;
  fog: NivelFog;
}

export function anotarSkills(): SkillAnotada[] {
  const personaje = useCharacterStore.getState().personaje;
  const desbloq = new Set(personaje.skillsDesbloqueadas);
  const puntos = personaje.puntosSkillDisponibles;

  return SKILLS_TODAS.map((skill): SkillAnotada => {
    if (desbloq.has(skill.id)) {
      return { skill, estado: 'desbloqueada', motivo: null, fog: 'ninguno' };
    }

    const prereqsFaltantes = skill.requisitos.filter((r) => !desbloq.has(r));
    const todosCumplidos = prereqsFaltantes.length === 0;
    const algunPrereqHecho =
      skill.requisitos.length === 0 || skill.requisitos.some((r) => desbloq.has(r));

    // Skills "lejanas": ningún prereq hecho Y no es tier inicial.
    const esLejana = !algunPrereqHecho && skill.tier > 1;
    const esSpoiler = esLejana && skill.tier >= 4;

    if (!todosCumplidos) {
      const nombres = prereqsFaltantes
        .map((id) => obtenerSkill(id)?.nombre ?? id)
        .join(', ');
      return {
        skill,
        estado: 'bloqueada',
        motivo: `Requiere: ${nombres}.`,
        fog: esSpoiler ? 'spoiler' : esLejana ? 'niebla' : 'ninguno',
      };
    }

    if (puntos < skill.costo) {
      const faltan = skill.costo - puntos;
      return {
        skill,
        estado: 'bloqueada',
        motivo: `Faltan ${faltan} punto${faltan !== 1 ? 's' : ''}.`,
        fog: 'ninguno',
      };
    }

    return { skill, estado: 'disponible', motivo: null, fog: 'ninguno' };
  });
}

export function anotarRama(rama: SkillDefinicion['rama']): SkillAnotada[] {
  return anotarSkills().filter((a) => a.skill.rama === rama);
}

export class SkillNoDisponibleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SkillNoDisponibleError';
  }
}

export function desbloquearSkill(skillId: string): SkillDefinicion {
  const skill = obtenerSkill(skillId);
  if (!skill) throw new SkillNoDisponibleError(`Skill inexistente: ${skillId}`);

  const character = useCharacterStore.getState();
  const personaje = character.personaje;

  if (personaje.skillsDesbloqueadas.includes(skillId)) {
    throw new SkillNoDisponibleError(`Skill ya desbloqueada: ${skill.nombre}`);
  }

  const faltantes = skill.requisitos.filter(
    (r) => !personaje.skillsDesbloqueadas.includes(r),
  );
  if (faltantes.length > 0) {
    throw new SkillNoDisponibleError(
      `Faltan prerequisitos: ${faltantes.join(', ')}`,
    );
  }

  if (personaje.puntosSkillDisponibles < skill.costo) {
    throw new SkillNoDisponibleError('Puntos de habilidad insuficientes.');
  }

  useCharacterStore.setState((s) => ({
    personaje: {
      ...s.personaje,
      puntosSkillDisponibles: s.personaje.puntosSkillDisponibles - skill.costo,
      skillsDesbloqueadas: [...s.personaje.skillsDesbloqueadas, skillId],
    },
  }));

  aplicarEfectos(skill.efectosAlDesbloquear);

  useCharacterStore.getState().persistir();

  return skill;
}
